import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Clock, Tag, User } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => { if (Array.isArray(r.data)) setPosts(r.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Blog — AI, ML & Career Insights"
        description="Tutorials, news, and career insights on AI, Machine Learning, and Data Science from Institute of Analytics USA. Daily AI-curated posts plus expert articles."
        path="/blog"
      />
      <div className="max-w-5xl mx-auto px-6 py-24">
        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Insights</p>
        <h1 data-testid="blog-heading" className="text-4xl md:text-5xl font-black tracking-tighter mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          AI Career Insights & News
        </h1>
        <p className="text-lg text-white/80 mb-16 max-w-2xl">Expert perspectives on AI, ML, and Data Science careers from Institute of Analytics USA&trade;.</p>

        <div className="space-y-0 border border-[#262626]">
          {posts.map((post, i) => (
            <Link
              key={post.slug || i}
              to={`/blog/${post.slug}`}
              data-testid={`blog-post-${i}`}
              className="block p-8 border-b border-[#262626] bg-[#111111] hover:bg-[#1a1a1a] transition-colors group"
            >
              <div className="flex items-center gap-3 mb-3">
                {(post.tags || []).map((tag, j) => (
                  <span key={j} className="text-xs font-mono font-bold px-2 py-1 bg-[#F59E0B]/15 text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{tag}</span>
                ))}
              </div>
              <h2 className="text-xl font-bold tracking-tight text-white group-hover:text-[#0077B6] transition-colors mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {post.title}
              </h2>
              <p className="text-base text-zinc-300 leading-relaxed mb-4">{post.excerpt}</p>
              <div className="flex items-center gap-4 text-sm text-zinc-400">
                <span className="flex items-center gap-1"><User className="w-4 h-4" strokeWidth={1.5} /> {post.author}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" strokeWidth={1.5} /> {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </Link>
          ))}
          {posts.length === 0 && <div className="p-16 text-center text-zinc-400">No posts yet. Check back soon!</div>}
        </div>
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(r => {
        if (r.data && r.data.content && !r.data.detail) {
          setPost(r.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400">Loading...</div>;
  if (!post) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <p className="text-2xl font-bold text-[#F59E0B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Post Not Found</p>
        <p className="text-base text-zinc-400 mb-6">This blog post may have been moved or deleted.</p>
        <Link to="/blog" className="brand-btn text-sm px-6 py-3 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to All Posts
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title={post.title}
        description={post.excerpt || post.content?.replace(/<[^>]+>/g, '').slice(0, 155)}
        path={`/blog/${slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt || '',
          "datePublished": post.created_at,
          "author": {"@type": "Organization", "name": post.author || "Institute of Analytics USA"},
          "publisher": {"@type": "Organization", "name": "Institute of Analytics USA", "logo": {"@type":"ImageObject","url":"https://americaninstituteofanalytics.com/ioa_logo_dark.svg"}},
          "mainEntityOfPage": `https://americaninstituteofanalytics.com/blog/${slug}`,
          "keywords": (post.tags || []).join(", ")
        }}
      />
      <div className="max-w-6xl mx-auto px-6 py-24">
        <Link to="/blog" className="flex items-center gap-2 text-base text-zinc-300 hover:text-[#0077B6] mb-8" data-testid="back-to-blog">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} /> Back to Insights
        </Link>
        <div className="flex gap-2 mb-4">
          {(post.tags || []).map((tag, j) => (
            <span key={j} className="text-xs font-mono font-bold px-2 py-1 bg-[#F59E0B]/15 text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{tag}</span>
          ))}
        </div>
        <h1 data-testid="blog-post-title" className="text-3xl md:text-4xl font-black tracking-tighter mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {post.title}
        </h1>
        <div className="flex items-center gap-4 text-base text-zinc-400 mb-10">
          <span className="flex items-center gap-1.5"><User className="w-4 h-4" strokeWidth={1.5} /> {post.author}</span>
          <span className="font-mono text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{new Date(post.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
        <div className="prose prose-invert max-w-none">
          <div className="text-base text-white/85 leading-relaxed blog-content" dangerouslySetInnerHTML={{ __html: post.content.includes('<') ? post.content : post.content.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('') }} />
        </div>
        <div className="mt-16 border-t border-[#262626] pt-8">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Ready to Start?</p>
          <p className="text-base text-zinc-300 mb-6">Join 200+ graduates who are now working in AI, ML, and Data Science.</p>
          <Link to="/register" className="brand-btn text-base px-8 py-3 inline-flex items-center gap-2">
            Enroll Now <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
